import { getEmailByJwtAuthTokenCookie } from '../genericUtils/analyticsUtils';
import { saltedHash } from '../genericUtils/encodeDecodeUtils';
import { RootState } from '../stateManager/store';
import { getCountryCodeFromMetaTag } from '../utilFuncs/contextAwareUtils';
import { keyCodes } from '../keyboardUtils/keyboardUtils';
import { mscGet, mscSet } from '../utilFuncs/mscStorageClient';

interface ACDL {
  push: (event: object) => void;
}

declare global {
  interface Window {
    adobeDataLayer: ACDL & Record<string, any>[];
    pageViewContent: Record<string, any>;
    containerMap: Record<string, string>;
    pageViewTracked: boolean;
    containerMappingRefresh: () => void;
    didomiOnReady?: any[];
    didomiState?: { didomiVendorsEnabled?: any };
  }
}

/**
 * Push an event to the Adobe Client Data Layer
 * @param eventName The name of the event
 * @param eventData The data associated with the event
 */
export const trackEvent = (eventName: string, eventData: object) => {
  console.log('trackEvent called — eventName:', eventName, 'eventData:', eventData);
  window.didomiOnReady = window.didomiOnReady || [];
  console.log('trackEvent — didomiOnReady before push:', window.didomiOnReady);
  window.didomiOnReady.push(() => {
    console.log('trackEvent — didomiOnReady callback fired for event:', eventName);
    console.log('trackEvent — didomiVendorsEnabled:', window.didomiState?.didomiVendorsEnabled);
    if (window.adobeDataLayer && typeof window.adobeDataLayer?.push === 'function') {
      const payload = {
        event: eventName,
        ...eventData,
      };
      console.log('trackEvent — adobeDataLayer payload:', payload);
      window.adobeDataLayer?.push(payload);
      console.log('trackEvent — adobeDataLayer push completed for event:', eventName);
    } else {
      console.log('Adobe Client Data Layer is not initialized.');
    }
  });
  console.log('trackEvent — didomiOnReady.push completed for event:', eventName);
};
const calculatePageValues = (): { pc: string; psc: string; pssc: string; vpn: string } => {
  const urlValue = window.location.pathname;
  const urlParts: string[] = urlValue.split('/');
  let index = 1;

  let pc = urlParts[index] ? urlParts[index] : '';
  //remove language or currency from page category if exist
  if (pc == window.pageViewContent['page_language'] || window.pageViewContent['currency']) {
    index = 2;
    pc = urlParts[index] ? urlParts[index] : '';
  }
  let psc = urlParts[index + 1] ? urlParts[index + 1] : '';
  let pssc = urlParts[index + 2] ? urlParts[index + 2] : '';

  const pageViewContent = window.pageViewContent || {};

  if (mscGet<string>('session', 'virtualPageName')?.includes('booking')) {
    mscSet('session', 'prev_page', mscGet<string>('session', 'virtualPageName') || '');
  }

  const vpn =
    pageViewContent?.['page_category'] +
    (pageViewContent?.['page_sub_category'] ? '/' + pageViewContent?.['page_sub_category'] : '') +
    (pageViewContent?.['page_sub_subcategory']
      ? '/' + pageViewContent?.['page_sub_subcategory']
      : '');

  if (vpn === 'home') {
    pc = vpn;
    psc = '';
    pssc = '';
  }
  const virtualPageName = pc + (psc ? '/' + psc : '') + (pssc ? '/' + pssc : '');
  mscSet('session', 'virtualPageName', virtualPageName);
  if (mscGet<string>('session', 'virtualPageName') && !mscGet<string>('session', 'prev_page'))
    mscSet('session', 'prev_page', mscGet<string>('session', 'virtualPageName') || '');

  return { pc, psc, pssc, vpn };
};

const updateContainerList = (): string | null => {
  // Check if pageViewContent and container_list exist
  if (!window.pageViewContent || typeof window.pageViewContent['container_list'] !== 'string') {
    console.log('pageViewContent or container_list is not defined or not a string');
    return null;
  }

  // Get the container_list string and split it into an array
  const containerListString = window.pageViewContent['container_list'].trim();
  let containerListArray = containerListString.split(',').map((item) => item.trim());

  // Filter out items containing 'notAssignedType_notAssignedName'
  containerListArray = containerListArray.filter((item) => {
    return !item.includes('notAssignedType_notAssignedName');
  });

  // Select all elements with data-container-hash attribute
  const elements = document.querySelectorAll('[data-container-hash]');

  elements.forEach((element) => {
    const containerHash = (element as HTMLElement).getAttribute('data-container-hash');

    // If element has the class "hide" and containerHash exists in containerMap
    if (containerHash && (element as HTMLElement).classList.contains('hide')) {
      const correspondingValue = window.containerMap[containerHash];

      if (correspondingValue) {
        // Remove the corresponding value from containerListArray
        containerListArray = containerListArray.filter((item) => item !== correspondingValue);
      }
    }
  });

  // Join the updated array back into a string
  let updatedContainerList = containerListArray.join(';');

  //
  // Step 1: Extract prefix-to-value mappings from containerMap
  const prefixMap: Record<string, string> = {};
  Object.values(window.containerMap).forEach((value) => {
    const prefix = value.split('_')[0]; // Extract the prefix
    prefixMap[prefix] = value;
  });

  // Step 2: Replace elements in containerList using the prefix
  updatedContainerList = updatedContainerList
    .split(';')
    .map((item) => {
      const prefix = item.split('_')[0]; // Extract prefix from containerList item
      return prefixMap[prefix] || item; // Replace if a matching prefix is found
    })
    .join(';');

  window.pageViewContent['container_list'] = updatedContainerList;

  let reorderedList = '';

  if (updatedContainerList) {
    reorderedList = updatedContainerList
      .split(';')
      .map((item, index) => {
        const parts = item.split('_');
        parts[0] = (index + 1).toString(); // Replace the number prefix
        return parts.join('_');
      })
      .join(';');
  }

  return reorderedList;
};
let userState: RootState;
export const constructPageViewData = (): Record<string, any> => {
  const { pc, psc, pssc } = calculatePageValues();
  let prevPage = null;
  if (mscGet<string>('session', 'prev_page') && mscGet<string>('session', 'prev_page2'))
    prevPage = mscGet<string>('session', 'prev_page') ? mscGet<string>('session', 'prev_page') : '';
  else if (
    mscGet<string>('session', 'prev_page') &&
    !mscGet<string>('session', 'prev_page2') &&
    mscGet<string>('session', 'prev_page') != mscGet<string>('session', 'virtualPageName')
  )
    prevPage = mscGet<string>('session', 'prev_page') ? mscGet<string>('session', 'prev_page') : '';

  if (prevPage) mscSet('session', 'started_at', prevPage || '');

  const pageData = {
    page: {
      ...window.pageViewContent,
      country: getCountryCodeFromMetaTag().toLowerCase(),
      page_category: pc,
      page_sub_category: psc,
      page_sub_subcategory: pssc,
      virtual_page_name: pc + (psc ? '/' + psc : '') + (pssc ? '/' + pssc : ''),
      ref_container: mscGet<string>('session', 'lastClickedContainerId')
        ? mscGet<string>('session', 'lastClickedContainerId')
        : 'noref',
      container_list: updateContainerList(),
      prev_page: prevPage || '',
      website_area: 'public area',
    },
    user: {
      user_id:
        getEmailByJwtAuthTokenCookie() != ''
          ? saltedHash(getEmailByJwtAuthTokenCookie())
          : 'not available',
      user_status: 'not available', // TODO: Update when private area is integrated
      user_typology: saltedHash(mscGet<string>('session', 'tsk') || '') || 'not available',
      visitor_is_customer:
        mscGet<string>('session', 'vic') === 'true'
          ? 'yes'
          : mscGet<string>('session', 'vic') === null
          ? 'not available'
          : 'no',
      membership_status:
        getEmailByJwtAuthTokenCookie() != ''
          ? (userState?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo &&
              userState?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo?.length >
                0) ||
            (userState?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo &&
              userState?.bookingStepsStore?.secondCabinSelections?.voyagersMembersInfo.length > 0)
            ? userState?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo[0]?.memberInfo
                ?.membership?.cardType
            : ''
          : 'not available',
    },
  };

  if (mscGet<string>('session', 'prev_page')) {
    const currentPage = pc + (psc ? '/' + psc : '') + (pssc ? '/' + pssc : '');

    const prevPage = mscGet<string>('session', 'prev_page');
    // Save page_category and page_subcategory to session storage
    mscSet('session', 'pre_pc', pc);
    mscSet('session', 'pre_psc', psc);

    const prevPage2 = mscGet<string>('session', 'prev_page2');
    if (window?.pageViewContent?.['page_category'] === 'booking' && psc === '' && prevPage2) {
      mscSet('session', 'prev_page', prevPage2);
    } else if (currentPage !== prevPage) {
      mscSet('session', 'prev_page2', currentPage);
      mscSet('session', 'prev_page', currentPage);
    }
  }

  return pageData;
};
// Track page view if not already tracked
const pageViewTracked = () => {
  const pageViewData = constructPageViewData();
  if (window?.pageViewContent?.['page_category'] !== 'booking') {
    trackEvent('page_view', pageViewData);
    window.pageViewTracked = true;
  }
};

// Initialize page view tracking after module loads to avoid circular dependency
// Use setTimeout to ensure all modules are loaded first
if (!window.pageViewTracked) {
  setTimeout(() => {
    if (window.pageViewTracked) {
      pageViewTracked();
    }
  }, 0);
}

export function saveContainerId(element: HTMLElement): void {
  let targetElement: HTMLElement | null | undefined = element;
  let containerIdHash: string | null = null;

  // Traverse up the DOM to find the closest container with a data-container-hash
  while (targetElement && !targetElement.hasAttribute('data-container-hash')) {
    targetElement = targetElement.parentElement;
  }

  // First attempt: Use `data-container-hash`
  if (targetElement?.hasAttribute('data-container-hash')) {
    containerIdHash = targetElement.getAttribute('data-container-hash');
  }

  // Second attempt: If not found, look for `data-react-component`
  if (!containerIdHash) {
    targetElement = element; // Reset to the original element
    while (targetElement && !targetElement.hasAttribute('data-react-component')) {
      targetElement = targetElement.parentElement;
    }

    if (targetElement?.hasAttribute('data-react-component')) {
      const reactComponentAttr = targetElement.getAttribute('data-react-component');
      if (reactComponentAttr) {
        // Extract the value after the last `-`
        const parts = reactComponentAttr.split('-');
        containerIdHash = parts.length > 1 ? parts.pop() ?? null : null;
      }
    }
  }

  // Save the result in sessionStorage
  if (containerIdHash && window.containerMap) {
    const myHashMap = window.containerMap as { [key: string]: string };
    const containerRef = myHashMap[containerIdHash];

    mscSet('session', 'lastClickedContainerId', containerRef || '');
  } else {
    mscSet('session', 'lastClickedContainerId', '');
  }
}

// Add event listeners for click and keydown events
document.addEventListener('click', (event: MouseEvent) => {
  const path = event.composedPath() as HTMLElement[];
  mscSet(
    'session',
    'lastClickedPath',
    path.map((el) => el.tagName)
  );

  let containerElement: HTMLElement | null = null;
  let containerIdHash: string | null = null;

  // Traverse event path to find the first ancestor that has `data-container-hash`
  for (const element of path) {
    if (element instanceof HTMLElement && element.hasAttribute('data-container-hash')) {
      containerElement = element;
      containerIdHash = containerElement.getAttribute('data-container-hash');

      if (containerIdHash && window.containerMap) {
        const myHashMap = window.containerMap as { [key: string]: string };
        const containerRef = myHashMap[containerIdHash];

        if (containerRef) {
          mscSet('session', 'lastClickedContainerId', containerRef);
          return;
        }
      }
    }
  }

  // If not found, continue searching for `data-react-component` in later elements
  for (const element of path) {
    if (element instanceof HTMLElement && element.hasAttribute('data-react-component')) {
      const reactComponentAttr = element.getAttribute('data-react-component');

      if (reactComponentAttr) {
        // Extract the value after the last hyphen, ensuring a string or null
        const parts = reactComponentAttr.split('-');
        containerIdHash = parts.length > 1 ? parts.pop() ?? null : null;

        if (containerIdHash && window.containerMap) {
          const myHashMap = window.containerMap as { [key: string]: string };
          const containerRef = myHashMap[containerIdHash];

          if (containerRef) {
            mscSet('session', 'lastClickedContainerId', containerRef);
            return;
          }
        }
      }
    }
  }

  // Check for "footer" or "header" in the class list of any ancestor elements
  //TODO: find a way to map the elements correctly in the container map instead of having them hard-coded
  for (const element of path) {
    if (element instanceof HTMLElement) {
      const classList = element.className.toLowerCase();
      if (classList.includes('footer')) {
        mscSet('session', 'lastClickedContainerId', 'footer');
        return;
      } else if (classList.includes('header')) {
        mscSet('session', 'lastClickedContainerId', 'header');
        return;
      } else if (classList.includes('navigationmenu')) {
        mscSet('session', 'lastClickedContainerId', 'navigationmenu');
        return;
      }
    }
  }

  // Default case: No valid container found
  mscSet('session', 'lastClickedContainerId', '');
});

document.addEventListener('keydown', (event: KeyboardEvent) => {
  // Handle Enter or Space key presses
  if (event.key === keyCodes.ENTER || event.key === keyCodes.SPACE) {
    const focusedElement = document.activeElement as HTMLElement;

    // Check if the focused element is an interactive element (e.g., link or button)
    if (focusedElement.tagName === 'A' || focusedElement.tagName === 'BUTTON') {
      saveContainerId(focusedElement);
    }
  }
});

if (!window.pageViewTracked) {
  window.pageViewTracked = true;
}
